import type { QueryFields, QueryFilter } from "@directus/sdk";
import type { Device, DeviceModel, VirtualDeviceConfiguration } from "@irriga-mais/common";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { VirtualDeviceConfigurationRecord } from "node_modules/@irriga-mais/common/dist/model/record/virtual-device-configuration";
import { apiService } from "@/api";
import type { AppSchema } from "@/api/client";
import type {
  AUTAccount,
  AUTDevice,
  AUTIrrigationPlan,
  AUTIrrigationPlanStepTank,
  AUTProject,
  AUTProjectTank,
  AUTProperty,
  AUTPropertyDevice,
  AUTReservoir,
  AUTSectorCrop,
  AUTTank,
  AUTWaterPump,
} from "@/api/queries/account";
import { buildRainfallSummary } from "@/utils";
import {
  getProjectBackwashVirtualDeviceConfig,
  getReservoirVirtualDeviceConfig,
  getSectorVirtualDeviceConfig,
  getWaterPumpVirtualDeviceConfig,
} from "@/utils/device";
import { isLICDeviceModel } from "@/utils/device-model";
import { withOperationHandlingAtom } from "../operations";
import { accountUserTreeAtom } from "./accounts";
import { currentLICPacketsByPropertyAtom } from "./lic-domain";

export const selectedPropertyIdAtom = atomWithStorage<string | null>("selectedPropertyId", null, undefined, {
  getOnInit: true,
});
selectedPropertyIdAtom.debugLabel = "selectedPropertyIdAtom";

export const currentAccountAtom = atom<AUTAccount | null>((get) => {
  const tree = get(accountUserTreeAtom);
  return tree?.account || null;
});
currentAccountAtom.debugLabel = "currentAccountAtom";

export const propertiesAtom = atom<AUTProperty[]>((get) => {
  const account = get(currentAccountAtom);
  return account?.properties || [];
});
propertiesAtom.debugLabel = "propertiesAtom";

export const selectedPropertyAtom = atom<AUTProperty | null>((get) => {
  const properties = get(propertiesAtom);
  const selectedId = get(selectedPropertyIdAtom);
  return properties.find((p) => p.id === selectedId) || null;
});
selectedPropertyAtom.debugLabel = "selectedPropertyAtom";

export const projectsAtom = atom<AUTProject[]>((get) => {
  const property = get(selectedPropertyAtom);
  return property?.projects || [];
});
projectsAtom.debugLabel = "projectsAtom";

export const waterPumpsAtom = atom<AUTWaterPump[]>((get) => {
  const property = get(selectedPropertyAtom);
  return property?.water_pumps || [];
});
waterPumpsAtom.debugLabel = "waterPumpsAtom";

export const reservoirsAtom = atom<AUTReservoir[]>((get) => {
  const property = get(selectedPropertyAtom);
  return property?.reservoirs || [];
});
reservoirsAtom.debugLabel = "reservoirsAtom";

export const propertyDevicesAtom = atom<AUTPropertyDevice[]>((get) => {
  const property = get(selectedPropertyAtom);
  return property?.devices || [];
});
propertyDevicesAtom.debugLabel = "propertyDevicesAtom";

export const devicesAtom = atom<AUTDevice[]>((get) => {
  const propertyDevices = get(propertyDevicesAtom);
  return propertyDevices.map((pd) => pd.device).filter(Boolean) as AUTDevice[];
});
devicesAtom.debugLabel = "devicesAtom";

export const licDevicesAtom = atom<AUTDevice[]>((get) => {
  const devices = get(devicesAtom);
  return devices.filter((d) => isLICDeviceModel(d.model));
});
licDevicesAtom.debugLabel = "licDevicesAtom";

export const rainfallDataByPropertyAtom = atom((get) => {
  const packets = get(currentLICPacketsByPropertyAtom);
  const property = get(selectedPropertyAtom);
  const propertyDevices = get(propertyDevicesAtom);
  return buildRainfallSummary(packets, property, propertyDevices);
});
rainfallDataByPropertyAtom.debugLabel = "rainfallDataByPropertyAtom";

export const licsWithRainGaugeAtom = atom<number>((get) => {
  const rainfall = get(rainfallDataByPropertyAtom);
  return rainfall.gaugeCount;
});
licsWithRainGaugeAtom.debugLabel = "licsWithRainGaugeAtom";

export const deviceToLicMapAtom = atom<Map<string, string | null>>((get) => {
  const propertyDevices = get(propertyDevicesAtom);
  const map = new Map<string, string | null>();

  propertyDevices.forEach((pd) => {
    if (pd.device?.id) {
      const licPropertyDeviceId = pd.current_mesh_device_mapping?.lic_property_device;
      let licDeviceId: string | null = null;

      if (licPropertyDeviceId) {
        const licPropertyDevice = propertyDevices.find((lpd) => {
          if (typeof licPropertyDeviceId === "object" && licPropertyDeviceId !== null && "id" in licPropertyDeviceId) {
            return lpd.id === (licPropertyDeviceId as { id: string }).id;
          }
          if (typeof licPropertyDeviceId === "string") {
            return lpd.id === licPropertyDeviceId;
          }
          return false;
        });

        if (licPropertyDevice?.device?.id) {
          licDeviceId = licPropertyDevice.device.id;
        }
      }

      map.set(pd.device.id, licDeviceId);
    }
  });

  return map;
});
deviceToLicMapAtom.debugLabel = "deviceToLicMapAtom";

export const licMappedDevicesAtom = atom<{
  [licDeviceId: string]: Set<AUTDevice>;
}>((get) => {
  const deviceToLicMap = get(deviceToLicMapAtom);
  const devicesById = get(devicesByIdAtom);
  const result: { [licDeviceId: string]: Set<AUTDevice> } = {};

  for (const [deviceId, licDeviceId] of deviceToLicMap) {
    if (!licDeviceId) continue;
    if (!result[licDeviceId]) {
      result[licDeviceId] = new Set();
    }
    const device = devicesById(deviceId);
    if (device) {
      result[licDeviceId].add(device);
    }
  }

  return result;
});
licMappedDevicesAtom.debugLabel = "licMappedDevicesAtom";

export const irrigationPlansAtom = atom<AUTIrrigationPlan[]>((get) => {
  const projects = get(projectsAtom);
  return projects.flatMap((project) => project.irrigation_plans || []);
});
irrigationPlansAtom.debugLabel = "irrigationPlansAtom";

export const propertyByIdAtom = atom((get) => (propertyId: string): AUTProperty | null => {
  const properties = get(propertiesAtom);
  return properties.find((p) => p.id === propertyId) || null;
});
propertyByIdAtom.debugLabel = "propertyByIdAtom";

export const projectByIdAtom = atom((get) => (projectId: string): AUTProject | null => {
  const projects = get(projectsAtom);
  return projects.find((p) => p.id === projectId) || null;
});
projectByIdAtom.debugLabel = "projectByIdAtom";

export const irrigationPlanByIdAtom = atom((get) => (planId: string): AUTIrrigationPlan | null => {
  const plans = get(irrigationPlansAtom);
  return plans.find((p) => p.id === planId) || null;
});
irrigationPlanByIdAtom.debugLabel = "irrigationPlanByIdAtom";

/**
 * All irrigation plans controlled by a given LIC (localized_irrigation_controller).
 * Used primarily when checking plan conflicts before enabling schedule resumption.
 */
export const plansByLicIdAtom = atom((get) => (licId: string): AUTIrrigationPlan[] => {
  const properties = get(propertiesAtom);
  if (!licId) return [];
  const plans: AUTIrrigationPlan[] = [];
  for (const property of properties) {
    for (const project of property.projects) {
      if (project.localized_irrigation_controller === licId) {
        if (project.irrigation_plans?.length) {
          plans.push(...project.irrigation_plans);
        }
      }
    }
  }
  return plans;
});
plansByLicIdAtom.debugLabel = "plansByLicIdAtom";

export const devicesByIdAtom = atom((get) => (deviceId: string): AUTDevice | null => {
  const devices = get(devicesAtom);
  return devices.find((d) => d.id === deviceId) || null;
});
devicesByIdAtom.debugLabel = "devicesByIdAtom";

export const waterPumpsByIdAtom = atom((get) => (pumpId: string): AUTWaterPump | null => {
  const pumps = get(waterPumpsAtom);
  return pumps.find((p) => p.id === pumpId) || null;
});
waterPumpsByIdAtom.debugLabel = "waterPumpsByIdAtom";

export const reservoirsByIdAtom = atom((get) => (reservoirId: string): AUTReservoir | null => {
  const reservoirs = get(reservoirsAtom);
  return reservoirs.find((r) => r.id === reservoirId) || null;
});
reservoirsByIdAtom.debugLabel = "reservoirsByIdAtom";

// ─── Tank domain atoms (property-scoped) ───────────────────────────────────

export const tanksAtom = atom<AUTTank[]>((get) => {
  const property = get(selectedPropertyAtom);
  return property?.tanks || [];
});
tanksAtom.debugLabel = "tanksAtom";

export const tanksByIdAtom = atom((get) => (tankId: string): AUTTank | null => {
  const tanks = get(tanksAtom);
  return tanks.find((t) => t.id === tankId) || null;
});
tanksByIdAtom.debugLabel = "tanksByIdAtom";

// ─── Fertigation domain atoms (project/sector/step-scoped) ─────────────────

export const projectTanksByProjectIdAtom = atom((get) => (projectId: string): AUTProjectTank[] => {
  const projects = get(projectsAtom);
  const project = projects.find((p) => p.id === projectId);
  return project?.project_tanks || [];
});
projectTanksByProjectIdAtom.debugLabel = "projectTanksByProjectIdAtom";

/**
 * Active project-tank assignments for a given tank ID.
 * Scans all projects and returns entries where `tank.id === tankId` and `end_date === null`.
 * Used to warn the user before deleting a tank that is still assigned to projects.
 */
export const projectTanksByTankIdAtom = atom((get) => (tankId: string): AUTProjectTank[] => {
  const projects = get(projectsAtom);
  const activeAssignments: AUTProjectTank[] = [];
  for (const project of projects) {
    for (const pt of project.project_tanks || []) {
      if (pt.tank?.id === tankId && pt.end_date === null) {
        activeAssignments.push(pt);
      }
    }
  }
  return activeAssignments;
});
projectTanksByProjectIdAtom.debugLabel = "projectTanksByProjectIdAtom";

export const sectorCropsBySectorIdAtom = atom((get) => (sectorId: string): AUTSectorCrop[] => {
  const projects = get(projectsAtom);
  for (const project of projects) {
    for (const sector of project.sectors || []) {
      if (sector.id === sectorId) {
        return sector.sector_crops || [];
      }
    }
  }
  return [];
});
sectorCropsBySectorIdAtom.debugLabel = "sectorCropsBySectorIdAtom";

export const stepTanksByStepIdAtom = atom((get) => (stepId: string): AUTIrrigationPlanStepTank[] => {
  const projects = get(projectsAtom);
  for (const project of projects) {
    for (const plan of project.irrigation_plans || []) {
      for (const step of plan.steps || []) {
        if (step.id === stepId) {
          return step.irrigation_plan_step_tanks || [];
        }
      }
    }
  }
  return [];
});
stepTanksByStepIdAtom.debugLabel = "stepTanksByStepIdAtom";

export const sectorsByProjectIdAtom = atom((get) => (projectId: string): AUTProject["sectors"] => {
  const projects = get(projectsAtom);
  const project = projects.find((p) => p.id === projectId);
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  return project?.sectors?.sort((a, b) => collator.compare(a.name, b.name)) || [];
});
sectorsByProjectIdAtom.debugLabel = "sectorsByProjectIdAtom";

export const irrigationPlansByProjectIdAtom = atom((get) => (projectId: string): AUTIrrigationPlan[] => {
  const projectById = get(projectByIdAtom);
  const project = projectById(projectId);
  return project?.irrigation_plans?.sort((a, b) => a.start_time.localeCompare(b.start_time)) || [];
});
irrigationPlansByProjectIdAtom.debugLabel = "irrigationPlansByProjectIdAtom";

export type VirtualDeviceConfigurationWithModel = VirtualDeviceConfiguration<{
  device: Device<{
    model: DeviceModel;
  }>;
}>;

//The base atom that holds the actual data
const virtualDeviceConfigurationDataAtom = atom<VirtualDeviceConfigurationWithModel[]>([]);
virtualDeviceConfigurationDataAtom.debugLabel = "virtualDeviceConfigurationDataAtom";

// 2. The "Action" atom to handle fetching
/**
 * Virtual Device Configuration of the selected property.
 * get - returns the cached data
 * set - fetches the data from the API and caches it
 */
export const virtualDeviceConfigurationAtom = atom(
  (get) => get(virtualDeviceConfigurationDataAtom), // The "Read" part
  async (get, set) => {
    // The "Write" part
    const selectedPropertyId = get(selectedPropertyIdAtom);
    if (!selectedPropertyId) return;
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: "fetchVirtualDeviceConfiguration",
        message: "Carregando configuração de dispositivo virtual...",
        displayLoading: true,
        displayError: true,
      },
      async () => {
        const items = await apiService.virtualDeviceConfiguration.query({
          fields: [
            "id",
            "date_created",
            "date_updated",
            "user_created",
            "user_updated",
            "mode",
            "turn_on_physical_output_index",
            "turn_off_physical_output_index",
            "monitoring_physical_input_index",
            "monitoring_input_mode",
            "equipment_type",
            "water_pump",
            "sector",
            "project_backwash",
            "reservoir",
            "start_date",
            "end_date",
            "date_created",
            "user_created",
            "date_updated",
            "user_updated",
            "metadata",
            "notes",
            {
              device: [
                "id",
                "identifier",
                "metadata",
                "notes",
                "user_created",
                "date_created",
                "user_updated",
                "date_updated",
                {
                  model: [
                    "id",
                    "date_created",
                    "user_created",
                    "date_updated",
                    "user_updated",
                    "metadata",
                    "notes",
                    "label",
                    "outputs",
                    "inputs",
                  ],
                },
              ],
            },
          ] as QueryFields<AppSchema, VirtualDeviceConfiguration>,
          filter: {
            _and: [
              {
                device: {
                  properties: {
                    property: { _eq: selectedPropertyId },
                  },
                },
              },
              {
                start_date: { _lte: new Date().toISOString() },
              },
              {
                _or: [{ end_date: { _null: true } }, { end_date: { _gt: new Date().toISOString() } }],
              },
            ],
          } as QueryFilter<AppSchema, VirtualDeviceConfiguration>,
        });
        set(virtualDeviceConfigurationDataAtom, items as unknown as VirtualDeviceConfigurationWithModel[]);
        return items;
      }
    );

    return result;
  }
);
virtualDeviceConfigurationAtom.debugLabel = "virtualDeviceConfigurationAtom";

/**
 * Mapping between water pump IDs and their virtual device configurations.
 * Returns a Map where keys are water pump IDs and values are the corresponding
 * virtual device configuration, or null if no configuration exists for that pump.
 */
export const waterPumpVirtualDeviceConfigMapAtom = atom<Map<string, VirtualDeviceConfigurationWithModel | null>>(
  (get) => {
    const waterPumps = get(waterPumpsAtom);
    const virtualDeviceConfigs = get(virtualDeviceConfigurationDataAtom);
    const map = new Map<string, VirtualDeviceConfigurationWithModel | null>();

    waterPumps.forEach((pump) => {
      const config = getWaterPumpVirtualDeviceConfig(pump.id, virtualDeviceConfigs);
      map.set(pump.id, config || null);
    });

    return map;
  }
);
waterPumpVirtualDeviceConfigMapAtom.debugLabel = "waterPumpVirtualDeviceConfigMapAtom";

/**
 * Mapping between sector IDs and their virtual device configurations.
 * Returns a Map where keys are sector IDs and values are the corresponding
 * virtual device configuration, or null if no configuration exists for that sector.
 */
export const sectorVirtualDeviceConfigMapAtom = atom<Map<string, VirtualDeviceConfigurationWithModel | null>>((get) => {
  const projects = get(projectsAtom);
  const virtualDeviceConfigs = get(virtualDeviceConfigurationDataAtom);
  const map = new Map<string, VirtualDeviceConfigurationWithModel | null>();

  projects.forEach((project) => {
    project.sectors?.forEach((sector) => {
      const config = getSectorVirtualDeviceConfig(sector.id, virtualDeviceConfigs);
      map.set(sector.id, config || null);
    });
  });

  return map;
});
sectorVirtualDeviceConfigMapAtom.debugLabel = "sectorVirtualDeviceConfigMapAtom";

/**
 * Mapping between reservoir IDs and their virtual device configurations.
 * Returns a Map where keys are reservoir IDs and values are the corresponding
 * virtual device configuration, or null if no configuration exists for that reservoir.
 */
export const reservoirVirtualDeviceConfigMapAtom = atom<Map<string, VirtualDeviceConfigurationWithModel | null>>(
  (get) => {
    const reservoirs = get(reservoirsAtom);
    const virtualDeviceConfigs = get(virtualDeviceConfigurationDataAtom);
    const map = new Map<string, VirtualDeviceConfigurationWithModel | null>();

    reservoirs.forEach((reservoir) => {
      const config = getReservoirVirtualDeviceConfig(reservoir.id, virtualDeviceConfigs);
      map.set(reservoir.id, config || null);
    });

    return map;
  }
);
reservoirVirtualDeviceConfigMapAtom.debugLabel = "reservoirVirtualDeviceConfigMapAtom";

/**
 * Mapping between project IDs and their backwash virtual device configurations.
 * Returns a Map where keys are project IDs and values are the corresponding
 * virtual device configuration for backwash, or null if no configuration exists for that project.
 */
export const projectBackwashVirtualDeviceConfigMapAtom = atom<Map<string, VirtualDeviceConfigurationWithModel | null>>(
  (get) => {
    const projects = get(projectsAtom);
    const virtualDeviceConfigs = get(virtualDeviceConfigurationDataAtom);
    const map = new Map<string, VirtualDeviceConfigurationWithModel | null>();

    projects.forEach((project) => {
      const config = getProjectBackwashVirtualDeviceConfig(project.id, virtualDeviceConfigs);
      map.set(project.id, config || null);
    });

    return map;
  }
);
projectBackwashVirtualDeviceConfigMapAtom.debugLabel = "projectBackwashVirtualDeviceConfigMapAtom";
